import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import AddItem from '../../ItemDetails/AddItem';
import BrowseCatalogue from './BrowseCatalogue';
import Product from './Product';
import GetHelp from './GetHelp';

import {
	ButtonGroup,
	ButtonWithLoader,
	Button,
	Drawer,
	DrawerBody,
	DrawerHeader,
	DrawerFooter,
	Container,
	FlexRow,
	FlexColumn,
	Search,
	Title,
	Subtitle,
	NotFound,
	CircularProgress,
	Bold,
} from '../../../../Styles';
import { Section, SearchRow, VerticalDivision, HorizontalDivision, Products } from './styles';

import { fetchInventoryItems, fetchAutocompleteDentiraProducts, addDentiraMapping, updateDentiraMapping } from '../../../../../actions/inventoryActionsV2';
import { updateSuccessMessage, updateErrorMessage } from '../../../../../actions/commonActions';

const MappingForm = ({ open, item = null, mapping = null, onSubmit = null, onClose = null, ...props }) => {
	const performingItemUpdate = Boolean(mapping);
	const { itemId = '', itemName = '' } = item || {};
	const { lab_products: labProducts = [], dentira_products: dentiraProducts = [] } = mapping || {};
	const [submitting, setSubmitting] = useState(false);
	const [labProductSearchText, setLabProductSearchText] = useState('');
	const [labProductSearchResults, setLabProductSearchResults] = useState([]);
	const [labProductSearchLoading, setLabProductSearchLoading] = useState(false);
	const [selectedLabProducts, setSelectedLabProducts] = useState(performingItemUpdate ? labProducts : itemId ? [{ item_id: itemId, name: itemName }] : []);
	const [dentiraProductSearchText, setDentiraProductSearchText] = useState('');
	const [dentiraProductSearchResults, setDentiraProductSearchResults] = useState([]);
	const [dentiraProductSearchLoading, setDentiraProductSearchLoading] = useState(false);
	const [selectedDentiraProducts, setSelectedDentiraProducts] = useState(dentiraProducts);

	const searchLabProducts = async () => {
		const { fetchInventoryItems } = props;
		setLabProductSearchLoading(true);

		let body = {
			page: 1,
			size: 10,
			search: labProductSearchText || '',
		};

		let products = [];

		if (labProductSearchText) {
			let response = await fetchInventoryItems(body);
			products = response?.products || [];
		}

		setLabProductSearchResults(products.map(({ supplier_product_id, name, description }) => ({ item_id: supplier_product_id, name, description })));
		setLabProductSearchLoading(false);
	};

	const searchDentiraProducts = async () => {
		const { fetchAutocompleteDentiraProducts } = props;
		setDentiraProductSearchLoading(true);

		let products = [];

		if (dentiraProductSearchText) {
			products = (await fetchAutocompleteDentiraProducts('', dentiraProductSearchText, 10, 1))?.products || [];
		}

		setDentiraProductSearchResults(products);
		setDentiraProductSearchLoading(false);
	};

	const checkErrors = () => {
		return !(selectedLabProducts?.length && selectedDentiraProducts?.length);
	};

	const handleSubmit = async () => {
		let { updateSuccessMessage, updateErrorMessage, addDentiraMapping, updateDentiraMapping } = props;

		if (checkErrors()) {
			updateErrorMessage(true, 'Clear errors before saving.');
			return;
		}

		setSubmitting(true);

		if (performingItemUpdate) {
			await updateDentiraMapping(mapping, { lab_products: selectedLabProducts, dentira_products: selectedDentiraProducts });
		} else {
			await addDentiraMapping({
				lab_products: selectedLabProducts,
				dentira_products: selectedDentiraProducts,
			});
		}

		setSubmitting(false);
		updateSuccessMessage(true, `Mapping ${performingItemUpdate ? 'updated' : 'added'} successfully.`);
		if (onSubmit) {
			onSubmit();
		}
		if (onClose) {
			onClose();
		}
	};

	useEffect(() => {
		searchLabProducts();
	}, [labProductSearchText]);

	useEffect(() => {
		searchDentiraProducts();
	}, [dentiraProductSearchText]);

	useEffect(() => {
		setLabProductSearchText('');
		setLabProductSearchResults([]);
		setDentiraProductSearchText('');
		setDentiraProductSearchResults([]);
	}, [open]);

	return (
		<Drawer
			anchor='bottom'
			open={open}
			onClose={() => {
				if (onClose) {
					onClose();
				}
			}}
		>
			<DrawerHeader>
				Add/Edit Item Mapping
				<ButtonGroup alignRight>
					<ButtonWithLoader
						secondary
						disabled={!(selectedLabProducts?.length && selectedDentiraProducts?.length)}
						loading={submitting}
						onClick={() => {
							handleSubmit();
						}}
					>
						Save
					</ButtonWithLoader>

					<Button
						outline
						secondary
						onClick={() => {
							if (onClose) {
								onClose();
							}
						}}
					>
						Cancel
					</Button>
				</ButtonGroup>
			</DrawerHeader>

			<DrawerBody>
				<Container>
					<FlexRow>
						<Section>
							<FlexRow>
								<FlexColumn>
									<Title>Lab Product</Title>

									<FlexRow>You can select one or multiple products that go together</FlexRow>
								</FlexColumn>
							</FlexRow>

							<HorizontalDivision />

							{selectedLabProducts?.length ? (
								<>
									<Subtitle>Selected Lab Products</Subtitle>

									<Products>
										{selectedLabProducts.map((product) => (
											<Product
												details={product}
												disabled={product.item_id === itemId}
												selected={true}
												onDelete={(product) => {
													setSelectedLabProducts(selectedLabProducts.filter((selectedProduct) => selectedProduct.item_id !== product.item_id));
												}}
											/>
										))}
									</Products>
								</>
							) : (
								<FlexRow>
									<FlexColumn>
										<NotFound lines={['No products selected']} />
									</FlexColumn>
								</FlexRow>
							)}

							<HorizontalDivision />

							{selectedLabProducts?.length === 0 || selectedDentiraProducts?.length <= 1 ? (
								<>
									<SearchRow>
										<Search
											placeholder={'Search By Lab Product ID'}
											searchText={labProductSearchText}
											onSearchHandler={(text) => setLabProductSearchText(text)}
											searchOnChange={true}
											enableDelayOnChange={true}
										/>
										Or <AddItem />
									</SearchRow>

									{labProductSearchLoading ? (
										<FlexRow>
											<CircularProgress showSpinner={true} />
										</FlexRow>
									) : labProductSearchResults?.length ? (
										<>
											<Subtitle>Search Results</Subtitle>

											<Products>
												{labProductSearchResults
													.map(
														(product) =>
															!selectedLabProducts.some((selectedProduct) => product.item_id === selectedProduct.item_id) && (
																<Product
																	details={product}
																	onSelect={(product) => {
																		setSelectedLabProducts([...selectedLabProducts, product]);
																	}}
																/>
															)
													)
													.filter((product) => product)}
											</Products>
										</>
									) : labProductSearchText ? (
										<FlexRow>
											<FlexColumn>
												<NotFound lines={['No products matches the search']} />
											</FlexColumn>
										</FlexRow>
									) : null}
								</>
							) : (
								<Bold>
									Multiple products selected in combination on Dentira's side. You can map them to only one of your products. If you are trying to map an add-on, please create a
									separate mapping where you map an add-on on lab's side to Dentira's side.
								</Bold>
							)}
						</Section>

						<VerticalDivision />

						<Section>
							<FlexRow>
								<FlexColumn>
									<Title>Dentira Equivalent</Title>

									<FlexRow>You can select either one primary product with add-ons or a group of compatible add-ons.</FlexRow>
								</FlexColumn>
							</FlexRow>

							<HorizontalDivision />

							{selectedDentiraProducts?.length ? (
								<>
									<Subtitle>Selected Dentira Equivalent</Subtitle>

									<Products>
										{selectedDentiraProducts.map((product) => (
											<Product
												details={product}
												selected={true}
												onDelete={(product) => {
													setSelectedDentiraProducts(selectedDentiraProducts.filter((selectedProduct) => selectedProduct.item_id !== product.item_id));
												}}
											/>
										))}
									</Products>
								</>
							) : (
								<FlexRow>
									<FlexColumn>
										<NotFound lines={['No products selected']} />
									</FlexColumn>
								</FlexRow>
							)}

							<HorizontalDivision />

							{selectedDentiraProducts?.length === 0 || selectedLabProducts?.length <= 1 ? (
								<>
									<SearchRow>
										<Search
											placeholder={'Search By Dentira Product ID'}
											searchText={dentiraProductSearchText}
											onSearchHandler={(text) => setDentiraProductSearchText(text)}
											searchOnChange={true}
											enableDelayOnChange={true}
										/>
										Or
										<BrowseCatalogue
											hidePrimaryProducts={selectedDentiraProducts.some((selectedProduct) => !selectedProduct?.add_on)}
											onSelect={(product) => {
												setSelectedDentiraProducts([...(!product?.add_on ? [product] : []), ...selectedDentiraProducts, ...(product?.add_on ? [product] : [])]);
											}}
										/>
									</SearchRow>

									{dentiraProductSearchLoading ? (
										<FlexRow>
											<CircularProgress showSpinner={true} />
										</FlexRow>
									) : dentiraProductSearchResults?.length ? (
										<>
											<Subtitle>Search Results</Subtitle>

											<Products>
												{dentiraProductSearchResults
													.map(
														(product) =>
															!selectedDentiraProducts.some(
																(selectedProduct) => product.item_id === selectedProduct.item_id || (!product?.add_on && !selectedProduct?.add_on)
															) && (
																<Product
																	details={product}
																	onSelect={(product) => {
																		setSelectedDentiraProducts([
																			...(!product?.add_on ? [product] : []),
																			...selectedDentiraProducts,
																			...(product?.add_on ? [product] : []),
																		]);
																	}}
																/>
															)
													)
													.filter((product) => product)}
											</Products>
										</>
									) : dentiraProductSearchText ? (
										<FlexRow>
											<FlexColumn>
												<NotFound lines={['No products matches the search']} />
											</FlexColumn>
										</FlexRow>
									) : null}
								</>
							) : (
								<Bold>
									Multiple products selected in combination on Labs side already. You can map them to only one Dentira product. If you are trying to map an add-on, please create a
									separate mapping where you map an add-on on lab's side to Dentira's side.
								</Bold>
							)}
						</Section>
					</FlexRow>
				</Container>
			</DrawerBody>

			<DrawerFooter>
				If you don't see what you're looking for you can reach out to us, we'll help you with it. Click on the 'Get Help' button to send us a message
				<ButtonGroup alignRight>
					<GetHelp products={selectedLabProducts} />
				</ButtonGroup>
			</DrawerFooter>
		</Drawer>
	);
};

const mapStateToProps = (state) => {
	return {};
};

const mapDispatch = { updateSuccessMessage, updateErrorMessage, fetchInventoryItems, fetchAutocompleteDentiraProducts, addDentiraMapping, updateDentiraMapping };

export default withRouter(connect(mapStateToProps, mapDispatch)(MappingForm));
